import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Circle, ArrowLeft, ArrowRight } from "lucide-react";
import { StartDateStep } from "./steps/StartDateStep";
import { PayTypeRateStep } from "./steps/PayTypeRateStep";
import { WorkingHoursStep } from "./steps/WorkingHoursStep";
import { ReviewConfirmStep } from "./steps/ReviewConfirmStep";
import { toast } from "@/components/ui/use-toast";

export interface SetupData {
  startDate: string | null;
  payType: "salary" | "hourly" | null;
  payRate: string | null;
  salaryUnit: "YEARLY" | "MONTHLY" | "WEEKLY" | null;
  payFrequency: "WEEKLY" | "FORTNIGHTLY" | "MONTHLY" | null;
  workingHours: Record<string, string> | null;
}

interface JobSetupWizardProps {
  employeeId: number;
  onComplete: () => void;
  onCancel: () => void;
}

const STEPS = [
  {
    id: "start_date",
    title: "Start Date",
    description: "When does the job begin?",
  },
  {
    id: "hours",
    title: "Working Hours",
    description:
      "Enter the number of hours the employee will work each day. Use 0 for non-working days.",
  },
  {
    id: "pay",
    title: "Pay & Rate",
    description:
      "Choose whether this employee will be paid a salary or hourly rate.",
  },
  {
    id: "review",
    title: "Review & Confirm",
    description:
      "Please review all the details below. Once confirmed, this setup cannot be easily changed.",
  },
];

export const JobSetupWizard: React.FC<JobSetupWizardProps> = ({
  employeeId,
  onComplete,
  onCancel,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [setupData, setSetupData] = useState<SetupData>({
    startDate: null,
    payType: null,
    payRate: null,
    salaryUnit: null,
    payFrequency: null,
    workingHours: null,
  });
  const [stepValidation, setStepValidation] = useState<Record<string, boolean>>(
    {},
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateSetupData = (updates: Partial<SetupData>) => {
    setSetupData((prev) => ({ ...prev, ...updates }));
  };

  const markStepValid = (stepId: string, isValid: boolean) => {
    setStepValidation((prev) => ({ ...prev, [stepId]: isValid }));
  };

  const canGoNext = () => {
    const currentStepId = STEPS[currentStep].id;
    return stepValidation[currentStepId] === true;
  };

  const canGoPrevious = () => {
    return currentStep > 0;
  };

  const handleNext = () => {
    if (canGoNext() && currentStep < STEPS.length - 1) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (canGoPrevious()) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleCancel = () => {
    // Show confirmation if user has entered data
    const hasData = Object.values(setupData).some((value) => value !== null);
    if (hasData) {
      const confirmed = window.confirm(
        "Are you sure you want to cancel? All entered data will be lost.",
      );
      if (!confirmed) return;
    }
    onCancel();
  };

  const getStepStatus = (stepIndex: number) => {
    if (stepIndex < currentStep) return "completed";
    if (stepIndex === currentStep) return "current";
    return "pending";
  };

  const renderCurrentStep = () => {
    const stepId = STEPS[currentStep].id;

    switch (stepId) {
      case "start_date":
        return (
          <StartDateStep
            employeeId={employeeId}
            value={setupData.startDate}
            onChange={(startDate) => updateSetupData({ startDate })}
            onValidation={(isValid) => markStepValid("start_date", isValid)}
          />
        );
      case "pay":
        return (
          <PayTypeRateStep
            employeeId={employeeId}
            values={{
              payType: setupData.payType,
              payRate: setupData.payRate,
              salaryUnit: setupData.salaryUnit,
              payFrequency: setupData.payFrequency,
            }}
            workingHours={setupData.workingHours}
            onChange={(updates) => updateSetupData(updates)}
            onValidation={(isValid) => markStepValid("pay", isValid)}
          />
        );
      case "hours":
        return (
          <WorkingHoursStep
            employeeId={employeeId}
            value={setupData.workingHours}
            onChange={(workingHours) => updateSetupData({ workingHours })}
            onValidation={(isValid) => markStepValid("hours", isValid)}
          />
        );
      case "review":
        return (
          <ReviewConfirmStep
            employeeId={employeeId}
            setupData={setupData}
            onValidation={(isValid) => markStepValid("review", isValid)}
            onSubmit={onComplete}
            isSubmitting={isSubmitting}
            setIsSubmitting={setIsSubmitting}
          />
        );
      default:
        return null;
    }
  };

  const progressPercentage = ((currentStep + 1) / STEPS.length) * 100;

  return (
    <Dialog
      open={true}
      onOpenChange={(open) => {
        if (!open) handleCancel();
      }}
      modal={true}
    >
      <DialogContent
        className="sm:max-w-[800px] max-h-[90vh] overflow-hidden flex flex-col"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Job Setup Wizard</DialogTitle>
          <DialogDescription>
            Set up the employee's job information step by step
          </DialogDescription>
        </DialogHeader>

        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm text-gray-600">
            <span>
              Step {currentStep + 1} of {STEPS.length}
            </span>
            <span>{Math.round(progressPercentage)}% complete</span>
          </div>
          <Progress value={progressPercentage} className="h-2" />
        </div>

        {/* Steps Overview */}
        <div className="flex justify-between items-center py-2">
          {STEPS.map((step, index) => {
            const status = getStepStatus(index);
            return (
              <div
                key={step.id}
                className="flex flex-col items-center space-y-1"
              >
                <div className="flex items-center">
                  {status === "completed" ? (
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  ) : (
                    <Circle
                      className={`h-6 w-6 ${
                        status === "current"
                          ? "text-blue-600 fill-blue-100"
                          : "text-gray-300"
                      }`}
                    />
                  )}
                </div>
                <div className="text-center">
                  <p
                    className={`text-xs font-medium ${
                      status === "current" ? "text-blue-600" : "text-gray-500"
                    }`}
                  >
                    {step.title}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Current Step Content */}
        <div className="flex-1 overflow-y-auto py-4">
          <div className="mb-4">
            <h3 className="text-lg font-semibold">
              {STEPS[currentStep].title}
            </h3>
            <p className="text-sm text-gray-600">
              {STEPS[currentStep].description}
            </p>
          </div>

          {renderCurrentStep()}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between pt-4 border-t">
          <div>
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={!canGoPrevious()}
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>

            {currentStep < STEPS.length - 1 && (
              <Button onClick={handleNext} disabled={!canGoNext()}>
                Next
                <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
